import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, App, Button, Card, Collapse, DatePicker, Empty, InputNumber, Segmented, Select, Space, Switch, Table, Tag,
  Tooltip, Typography,
} from 'antd';
import { FileExcelOutlined, InfoCircleOutlined, ReloadOutlined, WarningOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { enhanceColumns, toplamGenislik } from '../ortak/tableHelpers';
import { aramali } from './formParcalari';
import { LokasyonLinki, excelIndir, hata, para, tarih } from './ortak';

/**
 * S4 Aylık Rapor (26.10.2026) — /satis-sureci/aylik/*.
 *  (a) Sunumu yapılmamış / gecikmiş: süresi devam eden imzalı YS'si olan, aktif banka sözleşmesi ve (varsayılan)
 *      aktif sunum kaydı OLMAYAN aktif lokasyonlar; gün = bugün − en yeni YS'nin sisteme giriş tarihi.
 *  (b) Performans: ay içinde girilen yeni YS (kiralama: adet + yıllık kira) ve ay içinde başlayan banka sözleşmesi
 *      (satış: adet + bsk kira bedeli), lokasyon sorumlusu bazında; varsayılan yalnız özel mülk (2, 3).
 *  (c) Kira süresi 10 yıldan kısa imzalı YS sözleşmeleri + tarihi hatalı kayıtlar (veri değiştirilmez).
 */
const { Text } = Typography;

const ETIKET_RENK = { Gecikmiş: 'error', Sunumsuz: 'warning', Sunuldu: 'processing' };
// 30.10: etikete göre süzme; boş seçim = üçü birden, varsayılan sunulmuşlar hariç (eski davranış)
const ETIKETLER = [
  { value: 'gecikmis', label: 'Gecikmiş' }, { value: 'sunumsuz', label: 'Sunumsuz' }, { value: 'sunuldu', label: 'Sunuldu (banka yok)' }];
const kategoriParam = (k) => (k === null ? 'tumu' : (k || []).join(','));

function Sunumsuz({ api, secenek, ay }) {
  const { message } = App.useApp();
  const [esik, setEsik] = useState(secenek.gecikme_gun);
  const [kategoriler, setKategoriler] = useState([]);
  const [sorumlular, setSorumlular] = useState([]);
  const [kisiSecenek, setKisiSecenek] = useState([]);
  const [etiketler, setEtiketler] = useState(['gecikmis', 'sunumsuz']);
  const [yalnizAy, setYalnizAy] = useState(false);
  const [veri, setVeri] = useState(null);
  const [yukleniyor, setYukleniyor] = useState(false);
  const etiketSecim = useMemo(() => (etiketler.length ? etiketler : ETIKETLER.map((e) => e.value)), [etiketler]);
  const params = useMemo(() => ({
    esik, kategoriler: kategoriler.length ? kategoriler.join(',') : undefined,
    sorumlu: sorumlular.length ? sorumlular.join(',') : undefined, etiket: etiketSecim.join(','),
    ay: yalnizAy ? ay : undefined,
  }), [esik, kategoriler, sorumlular, etiketSecim, yalnizAy, ay]);
  const yukle = useCallback(() => {
    setYukleniyor(true);
    api.get('/aylik/sunumsuz', params).then((d) => {
      setVeri(d);
      // 02.11: sorumlu seçenekleri yalnız listede adı geçenler; seçili olup listeden düşen kişinin adı kaybolmasın
      setKisiSecenek((once) => {
        const yeni = d.sorumlu_secenekleri || [];
        const var_ = new Set(yeni.map((x) => x.value));
        return [...yeni, ...once.filter((x) => sorumlular.includes(x.value) && !var_.has(x.value))];
      });
    }).catch((e) => message.error(hata(e))).finally(() => setYukleniyor(false));
  }, [api, params, sorumlular, message]);
  useEffect(() => { yukle(); }, [yukle]);

  const kolonlar = useMemo(() => enhanceColumns([
    { title: 'Lokasyon', dataIndex: 'lokasyon_adi', width: 280, render: (v, r) => <LokasyonLinki id={r.lokasyon} ad={v} /> },
    { title: 'Durum', dataIndex: 'durum_adi', width: 150 },
    { title: 'Kategori', dataIndex: 'kategori_adi', width: 160 },
    { title: 'Sorumlu', dataIndex: 'sorumlu_adi', width: 160 },
    { title: 'YS giriş tarihi', dataIndex: 'ys_tarih', width: 115, render: tarih },
    { title: 'Gün', dataIndex: 'gun', width: 80, align: 'right' },
    { title: 'Etiket', dataIndex: 'etiket', width: 110, render: (v) => (
      <Tag color={ETIKET_RENK[v]} icon={v === 'Gecikmiş' ? <WarningOutlined /> : null}>{v}</Tag>) },
    { title: 'Son sunum', dataIndex: 'son_sunum', width: 110, render: tarih },
    { title: 'Sunum PDF', dataIndex: 'sunum_pdf', width: 100, render: (v) => (v ? <Tag color="success">Var</Tag> : <Tag>Yok</Tag>) },
    { title: 'YS bitiş', dataIndex: 'ys_bitis', width: 105, render: tarih },
  ]), []);
  const o = veri?.ozet;

  return (
    <div>
      <Space wrap style={{ marginBottom: 8 }}>
        <Space size={4}><Text>Gecikme eşiği</Text>
          <InputNumber min={1} max={3650} value={esik} onChange={(v) => v && setEsik(v)} suffix="gün" style={{ width: 120 }} /></Space>
        <Select {...aramali} mode="multiple" maxTagCount="responsive" placeholder="Kategori (tümü)" value={kategoriler}
          onChange={setKategoriler} options={secenek.kategoriler} style={{ minWidth: 220 }} />
        <Select {...aramali} mode="multiple" maxTagCount="responsive" placeholder="Etiket (tümü)" value={etiketler}
          onChange={setEtiketler} options={ETIKETLER} style={{ minWidth: 220 }} />
        <Select {...aramali} mode="multiple" maxTagCount="responsive" placeholder="Sorumlu (tümü)" value={sorumlular}
          onChange={setSorumlular} options={kisiSecenek} style={{ minWidth: 220 }} />
        <Space size={4}><Switch checked={yalnizAy} onChange={setYalnizAy} /><Text>Yalnız YS'si {dayjs(ay).format('MM.YYYY')} ayında girilenler</Text></Space>
        <Button icon={<ReloadOutlined />} onClick={yukle} loading={yukleniyor}>Yenile</Button>
        <Button icon={<FileExcelOutlined />} onClick={() => excelIndir(api, { bolum: 'sunumsuz', ...params }, `satis-sunumsuz-${dayjs().format('YYYYMMDD')}.xlsx`)
          .catch((e) => message.error(hata(e)))}>Excel</Button>
      </Space>
      {o && (
        <Space wrap style={{ marginBottom: 8 }}>
          <Tag>{o.toplam} lokasyon</Tag>
          {etiketSecim.includes('gecikmis') && <Tag color="error" icon={<WarningOutlined />}>{o.gecikmis} gecikmiş (≥ {veri.esik} gün)</Tag>}
          {etiketSecim.includes('sunumsuz') && <Tag color="warning">{o.sunumsuz} sunumsuz (eşik içinde)</Tag>}
          {etiketSecim.includes('sunuldu') && <Tag color="processing">{o.sunuldu} sunuldu, banka yok</Tag>}
          <Tag color="success">{o.pdf_hazir} sunum PDF'i hazır</Tag>
          <Tooltip title="Süresi devam eden imzalı (1/2/6) YS sözleşmesi olan, aktif banka sözleşmesi (fesih / çıkış tarihi boş) olmayan Aday / Kurulumu tamamlanmamış / tamamlanmış lokasyonlar. Gün = bugün − en yeni YS sözleşmesinin sisteme giriş tarihi. Sunum sonucu (kabul / ret) sunumlar tablosunda girilmediği için yalnız 'sunuldu / sunulmadı' ayrımı yapılır.">
            <InfoCircleOutlined style={{ color: '#8c8c8c' }} />
          </Tooltip>
        </Space>
      )}
      <Table rowKey="lokasyon" size="small" loading={yukleniyor} columns={kolonlar} dataSource={veri?.satirlar || []}
        scroll={{ x: toplamGenislik(kolonlar) }} pagination={{ pageSize: 50, showSizeChanger: true }}
        locale={{ emptyText: <Empty description="Kayıt yok" /> }} />
    </div>
  );
}

function Performans({ api, secenek, ay }) {
  const { message } = App.useApp();
  const [kategoriler, setKategoriler] = useState(secenek.ozel_mulk); // null = tümü
  const [veri, setVeri] = useState(null);
  const [yukleniyor, setYukleniyor] = useState(false);
  const params = useMemo(() => ({ ay, kategoriler: kategoriParam(kategoriler) }), [ay, kategoriler]);
  const yukle = useCallback(() => {
    setYukleniyor(true);
    api.get('/aylik/performans', params).then(setVeri).catch((e) => message.error(hata(e))).finally(() => setYukleniyor(false));
  }, [api, params, message]);
  useEffect(() => { yukle(); }, [yukle]);

  const kolonlar = useMemo(() => enhanceColumns([
    { title: 'Sorumlu', dataIndex: 'sorumlu_adi', width: 200 },
    { title: 'Kiralama (yeni YS)', dataIndex: 'kiralama_adet', width: 130, align: 'right' },
    { title: 'Yıllık kira toplamı', dataIndex: 'kiralama_yillik', width: 150, align: 'right', render: (v) => <span className="nf-amount">{para(v)}</span> },
    { title: 'Yıllık kira (adet başı)', dataIndex: 'kiralama_ort', width: 150, align: 'right', render: (v) => <span className="nf-amount">{v == null ? '—' : para(v)}</span> },
    { title: 'Satış (banka söz.)', dataIndex: 'satis_adet', width: 130, align: 'right' },
    { title: (
      <Tooltip title="Özel Mülk Satış Rapor ile aynı kural: lokasyonun ilk banka satışından sonraki satışlar (ek banka / kabin dahil) ya da satış tarihinde yürürlükteki mülk sözleşmesi yenileme ise yenileme.">
        Yeni / yenileme <InfoCircleOutlined />
      </Tooltip>), key: 'yeni_yenileme', dataIndex: 'satis_yeni', width: 130, align: 'right', render: (v, n) => `${v} / ${n.satis_yenileme}` },
    { title: 'Satış tutarı toplamı', dataIndex: 'satis_tutar', width: 150, align: 'right', render: (v) => <span className="nf-amount">{para(v)}</span> },
    { title: 'Satış tutarı (adet başı)', dataIndex: 'satis_ort', width: 150, align: 'right', render: (v) => <span className="nf-amount">{v == null ? '—' : para(v)}</span> },
  ], { search: false }), []);
  const kiraKolon = useMemo(() => enhanceColumns([
    { title: 'Giriş', dataIndex: 'eklenme', width: 100, render: tarih },
    { title: 'Söz. no', dataIndex: 'sozlesme_no', width: 100 },
    { title: 'Yer sahibi', dataIndex: 'ys_unvan', width: 200 },
    { title: 'Lokasyon', dataIndex: 'lokasyon_adi', width: 230, render: (v, r) => <LokasyonLinki id={r.lokasyon} ad={v} /> },
    { title: 'Kategori', dataIndex: 'kategori_adi', width: 150 },
    { title: 'Sorumlu', dataIndex: 'sorumlu_adi', width: 150 },
    { title: 'Yıllık kira', dataIndex: 'yillik_kira', width: 130, align: 'right', render: (v, r) => (
      <Tooltip title={r.aylik ? `Aylık ${para(r.kira)} × 12` : null}><span className="nf-amount">{para(v)}</span></Tooltip>) },
  ]), []);
  const satisKolon = useMemo(() => enhanceColumns([
    { title: 'Başlangıç', dataIndex: 'baslangic', width: 100, render: tarih },
    { title: 'Söz. no', dataIndex: 'sozlesme_no', width: 110 },
    { title: 'Banka', dataIndex: 'banka_adi', width: 140 },
    { title: 'Lokasyon', dataIndex: 'lokasyon_adi', width: 230, render: (v, r) => <LokasyonLinki id={r.lokasyon} ad={v} /> },
    { title: 'Kategori', dataIndex: 'kategori_adi', width: 150 },
    { title: 'Sorumlu', dataIndex: 'sorumlu_adi', width: 150 },
    { title: 'Tür', dataIndex: 'yenileme', width: 110, noFilter: true,
      filters: [{ text: 'Yeni', value: false }, { text: 'Yenileme', value: true }], onFilter: (v, r) => !!r.yenileme === v,
      render: (v, r) => (
      <Tooltip title={r.ilk_satis ? `Lokasyonun ilk satışı ${tarih(r.ilk_satis)}` : null}>
        {v ? <Tag color="purple">Yenileme</Tag> : <Tag color="green">Yeni</Tag>}
      </Tooltip>) },
    { title: 'Tutar', dataIndex: 'tutar', width: 130, align: 'right', render: (v) => <span className="nf-amount">{para(v)}</span> },
  ]), []);
  const t = veri?.toplam;

  return (
    <div>
      <Space wrap style={{ marginBottom: 8 }}>
        <Segmented value={kategoriler === null ? 'tumu' : 'secili'} onChange={(v) => setKategoriler(v === 'tumu' ? null : secenek.ozel_mulk)}
          options={[{ value: 'secili', label: 'Seçili kategoriler' }, { value: 'tumu', label: 'Tüm kategoriler' }]} />
        {kategoriler !== null && (
          <Select {...aramali} mode="multiple" maxTagCount="responsive" value={kategoriler} onChange={(v) => setKategoriler(v.length ? v : secenek.ozel_mulk)}
            options={secenek.kategoriler} style={{ minWidth: 260 }} />
        )}
        <Button icon={<ReloadOutlined />} onClick={yukle} loading={yukleniyor}>Yenile</Button>
        <Button icon={<FileExcelOutlined />} onClick={() => excelIndir(api, { bolum: 'performans', ...params }, `satis-performans-${ay}.xlsx`)
          .catch((e) => message.error(hata(e)))}>Excel</Button>
      </Space>
      {kategoriler === null && (
        <Alert type="warning" showIcon style={{ marginBottom: 8 }}
          message="Tüm kategorilerde kamu / belediye toplu sözleşmeleri (çok lokasyonlu tek YS sözleşmesi) tutarı şişirir; kişi kıyası için özel mülk (2, 3) önerilir." />
      )}
      <Table rowKey={(n) => n.sorumlu || 'atanmamis'} size="small" loading={yukleniyor} columns={kolonlar} dataSource={veri?.satirlar || []}
        pagination={false} scroll={{ x: toplamGenislik(kolonlar) }} locale={{ emptyText: <Empty description="Bu ay kayıt yok" /> }}
        summary={() => (t ? (
          <Table.Summary.Row>
            <Table.Summary.Cell index={0}><Text strong>Toplam</Text></Table.Summary.Cell>
            <Table.Summary.Cell index={1} align="right"><Text strong>{t.kiralama_adet}</Text></Table.Summary.Cell>
            <Table.Summary.Cell index={2} align="right"><Text strong>{para(t.kiralama_yillik)}</Text></Table.Summary.Cell>
            <Table.Summary.Cell index={3} align="right"><Text strong>{t.kiralama_ort == null ? '—' : para(t.kiralama_ort)}</Text></Table.Summary.Cell>
            <Table.Summary.Cell index={4} align="right"><Text strong>{t.satis_adet}</Text></Table.Summary.Cell>
            <Table.Summary.Cell index={5} align="right"><Text strong>{t.satis_yeni} / {t.satis_yenileme}</Text></Table.Summary.Cell>
            <Table.Summary.Cell index={6} align="right"><Text strong>{para(t.satis_tutar)}</Text></Table.Summary.Cell>
            <Table.Summary.Cell index={7} align="right"><Text strong>{t.satis_ort == null ? '—' : para(t.satis_ort)}</Text></Table.Summary.Cell>
          </Table.Summary.Row>) : null)} />
      <Collapse size="small" style={{ marginTop: 12 }} items={[
        { key: 'k', label: `Kiralama ayrıntısı (${veri?.kiralama?.length || 0} yeni YS sözleşmesi)`, children: (
          <Table rowKey="id" size="small" columns={kiraKolon} dataSource={veri?.kiralama || []} pagination={{ pageSize: 50 }}
            scroll={{ x: toplamGenislik(kiraKolon) }} />) },
        { key: 's', label: `Satış ayrıntısı (${veri?.satis?.length || 0} banka sözleşmesi satırı)`, children: (
          <Table rowKey="bsk_id" size="small" columns={satisKolon} dataSource={veri?.satis || []} pagination={{ pageSize: 50 }}
            scroll={{ x: toplamGenislik(satisKolon) }} />) },
      ]} />
      <Text type="secondary" style={{ display: 'block', marginTop: 8, fontSize: 12 }}>
        Kiralama: ay içinde sisteme girilen yeni YS sözleşmeleri (imzalı 1/2/6), yıllık kira = bağlı lokasyonların kira bedeli
        toplamı (aylık ödemeli ise × 12). Satış: ay içinde başlayan banka sözleşmeleri (Özel Mülk Satış Rapor'daki satış tarihi;
        yeni / yenileme ayrımı da o raporun kuralıyla), tutar = banka sözleşmesi yıllık kira bedeli. Adet başı = toplam ÷ adet.
        Kişi = lokasyon sorumlusu.
      </Text>
    </div>
  );
}

function KiraSuresi({ api, secenek, ay }) {
  const { message } = App.useApp();
  const [kapsam, setKapsam] = useState('ay');
  const [kategoriler, setKategoriler] = useState([]);
  const [veri, setVeri] = useState(null);
  const [yukleniyor, setYukleniyor] = useState(false);
  const params = useMemo(() => ({ ay, kapsam, kategoriler: kategoriler.length ? kategoriler.join(',') : undefined }), [ay, kapsam, kategoriler]);
  const yukle = useCallback(() => {
    setYukleniyor(true);
    api.get('/aylik/kira-suresi', params).then(setVeri).catch((e) => message.error(hata(e))).finally(() => setYukleniyor(false));
  }, [api, params, message]);
  useEffect(() => { yukle(); }, [yukle]);

  const ortak = [
    { title: 'Giriş', dataIndex: 'eklenme', width: 100, render: tarih },
    { title: 'Söz. no', dataIndex: 'sozlesme_no', width: 100 },
    { title: 'Yer sahibi', dataIndex: 'ys_unvan', width: 200 },
    { title: 'Lokasyon', dataIndex: 'lokasyon_adi', width: 230, render: (v, r) => <LokasyonLinki id={r.lokasyon} ad={v} /> },
    { title: 'Sorumlu', dataIndex: 'sorumlu_adi', width: 150 },
    { title: 'Başlangıç', dataIndex: 'baslangic', width: 100, render: tarih },
    { title: 'Bitiş', dataIndex: 'bitis', width: 100, render: tarih },
  ];
  const kolonlar = useMemo(() => enhanceColumns([
    ...ortak,
    { title: 'İmza', dataIndex: 'imza_adi', width: 160, render: (v, r) => (r.imza === 6 ? <Tag color="purple">{v}</Tag> : v) },
    { title: 'Süre', dataIndex: 'sure_ay', width: 110, align: 'right', render: (v, r) => r.sure_metni },
    { title: 'Yürürlükte', dataIndex: 'yururlukte', width: 100, render: (v) => (v ? <Tag color="processing">Evet</Tag> : <Tag>Bitti</Tag>) },
    { title: 'Yıllık kira', dataIndex: 'yillik_kira', width: 130, align: 'right', render: (v) => <span className="nf-amount">{para(v)}</span> },
  ]), []); // eslint-disable-line react-hooks/exhaustive-deps
  const hataKolon = useMemo(() => enhanceColumns([
    ...ortak,
    { title: 'Hata', dataIndex: 'hata', width: 190, render: (v) => <Tag color="error">{v}</Tag> },
    { title: 'Kayıttaki süre metni', dataIndex: 'sozlesmeSuresi', width: 150 },
  ]), []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div>
      <Space wrap style={{ marginBottom: 8 }}>
        <Segmented value={kapsam} onChange={setKapsam} options={[
          { value: 'ay', label: `${dayjs(ay).format('MM.YYYY')} ayında girilenler` }, { value: 'yururluk', label: 'Süresi devam edenler' }]} />
        <Select {...aramali} mode="multiple" maxTagCount="responsive" placeholder="Kategori (tümü)" value={kategoriler}
          onChange={setKategoriler} options={secenek.kategoriler} style={{ minWidth: 220 }} />
        <Button icon={<ReloadOutlined />} onClick={yukle} loading={yukleniyor}>Yenile</Button>
        <Button icon={<FileExcelOutlined />} onClick={() => excelIndir(api, { bolum: 'kira-suresi', ...params }, `satis-kira-suresi-${kapsam}-${ay}.xlsx`)
          .catch((e) => message.error(hata(e)))}>Excel</Button>
      </Space>
      {veri && (
        <Space wrap style={{ marginBottom: 8 }}>
          <Tag color="warning">{veri.ozet.kisa} sözleşme 10 yıldan kısa</Tag>
          {veri.ozet.kisa_yenileme ? <Tag color="purple">{veri.ozet.kisa_yenileme}'i yenileme</Tag> : null}
          <Tag>{veri.ozet.taranan} imzalı sözleşme tarandı</Tag>
          {veri.ozet.veri_hatasi ? <Tag color="error">{veri.ozet.veri_hatasi} tarihi hatalı</Tag> : null}
          <Text type="secondary">Süre = başlangıç → bitiş + 1 gün (ay); kayıttaki serbest metin süre kullanılmaz.</Text>
        </Space>
      )}
      <Table rowKey="id" size="small" loading={yukleniyor} columns={kolonlar} dataSource={veri?.satirlar || []}
        scroll={{ x: toplamGenislik(kolonlar) }} pagination={{ pageSize: 50, showSizeChanger: true }}
        locale={{ emptyText: <Empty description="Kayıt yok" /> }} />
      {veri?.veri_hatalari?.length ? (
        <Card size="small" style={{ marginTop: 12 }} title={<Space><WarningOutlined />Veri hataları — düzeltme sözleşme ekranından yapılır, burada veri değiştirilmez</Space>}>
          <Table rowKey="id" size="small" columns={hataKolon} dataSource={veri.veri_hatalari} pagination={false}
            scroll={{ x: toplamGenislik(hataKolon) }} />
        </Card>
      ) : null}
    </div>
  );
}

export default function AylikRapor({ api, secenek }) {
  const [ay, setAy] = useState(() => dayjs().format('YYYY-MM'));
  const [bolum, setBolum] = useState('sunumsuz');
  return (
    <div>
      <Space wrap style={{ marginBottom: 12 }}>
        <DatePicker picker="month" allowClear={false} inputReadOnly value={dayjs(`${ay}-01`)} format="MMMM YYYY"
          onChange={(v) => v && setAy(v.format('YYYY-MM'))} />
        <Segmented value={bolum} onChange={setBolum} options={[
          { value: 'sunumsuz', label: 'Sunumu yapılmamış / gecikmiş' },
          { value: 'performans', label: 'Performans' },
          { value: 'kira', label: 'Kira süresi < 10 yıl' },
        ]} />
      </Space>
      {bolum === 'sunumsuz' && <Sunumsuz api={api} secenek={secenek} ay={ay} />}
      {bolum === 'performans' && <Performans api={api} secenek={secenek} ay={ay} />}
      {bolum === 'kira' && <KiraSuresi api={api} secenek={secenek} ay={ay} />}
    </div>
  );
}
